import { createSignal } from "solid-js";
import { AsyncEventEmitter } from "@vladfrangu/async_event_emitter";
import { JSONParse, JSONStringify } from "json-with-bigint";
import { clientLog } from "../utils/clientLogger.js";
/**
 * All possible event client states.
 */
export var ConnectionState;
(function (ConnectionState) {
    ConnectionState[ConnectionState["Idle"] = 0] = "Idle";
    ConnectionState[ConnectionState["Connecting"] = 1] = "Connecting";
    ConnectionState[ConnectionState["Connected"] = 2] = "Connected";
    ConnectionState[ConnectionState["Disconnected"] = 3] = "Disconnected";
})(ConnectionState || (ConnectionState = {}));
/**
 * Simple wrapper around the Revolt websocket service.
 */
export class EventClient extends AsyncEventEmitter {
    options;
    #protocolVersion;
    #transportFormat;
    ping;
    #setPing;
    state;
    #setStateSetter;
    #socket;
    #heartbeatIntervalReference;
    #pongTimeoutReference;
    #connectTimeoutReference;
    #lastError;
    /**
     * Create a new event client.
     * @param protocolVersion Target protocol version
     * @param transportFormat Communication format
     * @param options Configuration options
     */
    constructor(protocolVersion, transportFormat = "json", options) {
        super();
        this.#protocolVersion = protocolVersion;
        this.#transportFormat = transportFormat;
        this.options = {
            heartbeatInterval: 30,
            // Bumped from 10s — under load (slow KeyDB/bonfire) Pong can take
            // longer than that, and a missed pong triggers a client reconnect.
            // 30s gives a 3x safety margin without making dead connections linger.
            pongTimeout: 30,
            // Bumped from 10s: the Ready payload (servers/channels/users/members)
            // can take a while to arrive under load, and this is now a hard deadline
            // for the whole handshake — too tight a value would cause reconnect loops.
            connectTimeout: 20,
            debug: false,
            ...options,
        };
        const [state, setState] = createSignal(ConnectionState.Idle);
        this.state = state;
        this.#setStateSetter = setState;
        const [ping, setPing] = createSignal(-1);
        this.ping = ping;
        this.#setPing = setPing;
        this.disconnect = this.disconnect.bind(this);
    }
    /**
     * Set the current state
     * @param state state
     */
    setState(state) {
        // Handshake completed successfully — cancel the watchdog so it can't fire
        // and tear down a healthy, fully-connected session.
        if (state === ConnectionState.Connected) {
            clearTimeout(this.#connectTimeoutReference);
            this.#connectTimeoutReference = undefined;
        }
        this.#setStateSetter(state);
        this.emit("state", state);
    }
    /**
     * Connect to the websocket service.
     * @param uri WebSocket URI
     * @param token Authentication token
     */
    connect(uri, token) {
        this.disconnect();
        this.#lastError = undefined;
        this.setState(ConnectionState.Connecting);
        // Handshake watchdog: hard deadline for reaching the Connected state
        // (Ready received). This is NOT cleared by intermediate frames — only by
        // setState(Connected). Without it, a socket that opens at the proxy layer
        // but never gets authenticated/Ready'd by bonfire leaves the client stuck
        // in Connecting forever ("reconnecting…" spinner that never resolves).
        // On timeout we tear down so the outer reconnect loop retries from scratch.
        this.#connectTimeoutReference = setTimeout(() => {
            clientLog("warn", "ws.handshake.timeout", {
                connectTimeoutSec: this.options.connectTimeout,
                state: this.state(),
            });
            this.disconnect();
        }, this.options.connectTimeout * 1e3);
        const url = new URL(uri);
        url.searchParams.set("version", this.#protocolVersion.toString());
        url.searchParams.set("format", this.#transportFormat);
        url.searchParams.set("token", token);
        // todo: pass-through ts as a configuration option
        // todo: then remove /settings/fetch from web client
        // todo: do the same for unreads
        // url.searchParams.append("ready", "users");
        // url.searchParams.append("ready", "servers");
        // url.searchParams.append("ready", "channels");
        // url.searchParams.append("ready", "members");
        // url.searchParams.append("ready", "emojis");
        // url.searchParams.append("ready", "voice_states");
        // url.searchParams.append("ready", "user_settings[ordering]");
        // url.searchParams.append("ready", "user_settings[notifications]");
        // url.searchParams.append("ready", "unreads or something");
        // url.searchParams.append("ready", "policy_changes");
        clientLog("info", "ws.connect.start", {
            url: url.toString().replace(/token=[^&]+/, "token=***"),
        });
        const connectStart = Date.now();
        this.#socket = new WebSocket(url);
        this.#socket.onopen = () => {
            clientLog("info", "ws.open", {
                elapsedMs: Date.now() - connectStart,
            });
            this.#heartbeatIntervalReference = setInterval(() => {
                this.send({ type: "Ping", data: +new Date() });
                this.#pongTimeoutReference = setTimeout(() => {
                    clientLog("warn", "ws.pong.timeout", {
                        pongTimeoutSec: this.options.pongTimeout,
                    });
                    this.disconnect();
                }, this.options.pongTimeout * 1e3);
            }, this.options.heartbeatInterval * 1e3);
        };
        this.#socket.onerror = (error) => {
            this.#lastError = { type: "socket", data: error };
            clientLog("error", "ws.error", {
                type: error?.type,
            });
            this.emit("error", error);
        };
        this.#socket.onmessage = (event) => {
            // NOTE: do NOT clear the handshake watchdog here. A stray frame (server
            // Ping, etc.) must not satisfy the deadline — only actually reaching the
            // Connected state (Ready) does, handled in setState().
            if (this.#transportFormat === "json") {
                if (typeof event.data === "string") {
                    this.handle(JSONParse(event.data));
                }
            }
        };
        let closed = false;
        this.#socket.onclose = (e) => {
            if (closed)
                return;
            closed = true;
            clientLog("warn", "ws.close", {
                code: e?.code,
                reason: e?.reason || undefined,
                wasClean: e?.wasClean,
                elapsedMs: Date.now() - connectStart,
                hadError: this.#lastError != null,
            });
            this.#socket = undefined;
            this.setState(ConnectionState.Disconnected);
            this.disconnect();
        };
    }
    /**
     * Disconnect the websocket client.
     */
    disconnect() {
        // Always clear timers FIRST, before the #socket guard. Otherwise onclose
        // (which sets #socket = undefined before calling disconnect) makes this
        // return early, leaking the heartbeat interval — it keeps firing forever,
        // calling send() on a dead socket and throwing "Socket closed, trying to
        // send." every tick. Each reconnect then stacks another zombie timer.
        clearInterval(this.#heartbeatIntervalReference);
        clearInterval(this.#connectTimeoutReference);
        clearInterval(this.#pongTimeoutReference);
        this.#heartbeatIntervalReference = undefined;
        this.#connectTimeoutReference = undefined;
        this.#pongTimeoutReference = undefined;
        if (!this.#socket)
            return;
        const socket = this.#socket;
        this.#socket = undefined;
        socket.close();
    }
    /**
     * Send an event to the server.
     * @param event Event
     */
    send(event) {
        if (this.options.debug)
            console.debug("[C->S]", event);
        // Guard instead of throwing: a stray send() on a closed/closing/connecting
        // socket is a normal race (e.g. heartbeat firing during teardown), not a
        // fatal error. Throwing here produced an unhandled window.error every tick.
        if (!this.#socket || this.#socket.readyState !== WebSocket.OPEN)
            return;
        this.#socket.send(JSONStringify(event));
    }
    /**
     * Handle events intended for client before passing them along.
     * @param event Event
     */
    handle(event) {
        if (this.options.debug)
            console.debug("[S->C]", event);
        switch (event.type) {
            case "Ping":
                this.send({
                    type: "Pong",
                    data: event.data,
                });
                return;
            case "Pong":
                clearTimeout(this.#pongTimeoutReference);
                this.#setPing(+new Date() - event.data);
                if (this.options.debug)
                    console.debug(`[ping] ${this.ping()}ms`);
                return;
            case "Error":
                this.#lastError = {
                    type: "revolt",
                    data: event.data,
                };
                this.emit("error", event);
                this.disconnect();
                return;
        }
        switch (this.state()) {
            case ConnectionState.Connecting:
                if (event.type === "Authenticated") {
                    // no-op
                }
                else if (event.type === "Ready") {
                    this.emit("event", event);
                    this.setState(ConnectionState.Connected);
                }
                else {
                    throw `Unreachable code. Received ${event.type} in Connecting state.`;
                }
                break;
            case ConnectionState.Connected:
                if (event.type === "Authenticated" || event.type === "Ready") {
                    throw `Unreachable code. Received ${event.type} in Connected state.`;
                }
                else {
                    this.emit("event", event);
                }
                break;
            default:
                throw `Unreachable code. Received ${event.type} in state ${this.state()}.`;
        }
    }
    /**
     * Last error encountered by events client
     */
    get lastError() {
        return this.#lastError;
    }
}
